import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Truck, User, CheckCircle2, Clock, Download, Stethoscope, X, Repeat } from "lucide-react";
import { ServiceAssign } from "@/components/ServiceAssign";
import { ReplaceSignup } from "@/components/ReplaceSignup";
import { ServiceHistory } from "@/components/ServiceHistory";

export function IscrittiDialog({ open, onOpenChange, service, onSaved }) {
  const [svc, setSvc] = useState(service);
  const [busy, setBusy] = useState(null);
  const [replacing, setReplacing] = useState(null);

  useEffect(() => { setSvc(service); }, [service]);

  if (!svc) return null;

  const toggle = async (uid, confirmed) => {
    setBusy(uid);
    try {
      const { data } = await api.post(`/services/${svc.id}/signups/${uid}/confirm`, { confirmed });
      setSvc(data);
      onSaved(data);
      toast.success(confirmed ? "Volontario confermato" : "Conferma revocata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const signups = svc.signups || [];
  const changed = (data) => { setSvc(data); onSaved(data); };
  const remove = async (s) => {
    if (!window.confirm(`Togliere ${s.name} dal servizio? Riceverà un avviso.`)) return;
    try {
      const { data } = await api.delete(`/services/${svc.id}/signups/${s.user_id}`);
      changed(data);
      toast.success(`${s.name} tolto dal servizio`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadAttendance = async (fmt) => {
    try {
      const res = await api.get(`/services/${svc.id}/attendance`, { params: { format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `presenze_${svc.event_name.replace(/\s+/g, "_")}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore durante l'esportazione presenze");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Iscritti · {svc.event_name}</DialogTitle>
          <DialogDescription>Conferma, togli o inserisci direttamente volontari, autisti e sanitari; esporta il foglio presenze.</DialogDescription>
        </DialogHeader>
        {signups.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="attendance-pdf" onClick={() => downloadAttendance("pdf")} className="rounded-full">
              <Download className="h-4 w-4 mr-1" /> Foglio presenze (PDF)
            </Button>
            <Button size="sm" variant="outline" data-testid="attendance-csv" onClick={() => downloadAttendance("csv")} className="rounded-full">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        )}
        <ServiceAssign svc={svc} onChanged={changed} />
        {signups.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">Nessun volontario iscritto a questo servizio.</p>
        ) : (
          <div className="space-y-2" data-testid="iscritti-list">
            {signups.map((s) => (
              <div key={s.user_id} data-testid={`iscritto-${s.user_id}`} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-1.5">
                    {s.slot === "ambulanza" || s.slot === "mezzo" ? <Truck className="h-4 w-4" /> : s.slot === "medico" || s.slot === "infermiere" ? <Stethoscope className="h-4 w-4 text-[#D32F2F]" /> : <User className="h-4 w-4" />}
                    {s.name}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge variant="secondary" className="text-[10px] capitalize">{s.slot === "mezzo" ? "autista mezzo" : s.slot}</Badge>
                    {(s.qualifications || []).filter((q) => !(q === "Medico" && s.slot === "medico") && !(q === "Infermiere" && s.slot === "infermiere")).map((q) => <Badge key={q} variant="outline" className="text-[10px]">{q}</Badge>)}
                  </div>
                  {(s.dates || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Date: {s.dates.map((d) => new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })).join(", ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" title="Sostituisci" data-testid={`replace-signup-${s.user_id}`} onClick={() => setReplacing(replacing === s.user_id ? null : s.user_id)} className="h-8 w-8">
                  <Repeat className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" title="Togli dal servizio" data-testid={`remove-signup-${s.user_id}`} onClick={() => remove(s)} className="h-8 w-8 text-destructive">
                  <X className="h-4 w-4" />
                </Button>
                {s.confirmed ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold"><CheckCircle2 className="h-4 w-4" /> Confermato</span>
                    <Button size="sm" variant="ghost" data-testid={`revoke-${s.user_id}`} disabled={busy === s.user_id} onClick={() => toggle(s.user_id, false)} className="text-muted-foreground">
                      Revoca
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" data-testid={`confirm-${s.user_id}`} disabled={busy === s.user_id} onClick={() => toggle(s.user_id, true)} className="rounded-full shrink-0">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Conferma
                  </Button>
                )}
                </div>
              </div>
              {replacing === s.user_id && (
                <ReplaceSignup svc={svc} signup={s} onCancel={() => setReplacing(null)} onDone={(d) => { setReplacing(null); changed(d); }} />
              )}
              </div>
            ))}
          </div>
        )}
        <ServiceHistory history={svc.history} />
      </DialogContent>
    </Dialog>
  );
}
